import { useCallback, useState } from "react";
import { Box, Button, Container, InputText, Icon, Stack, Text } from "@bookingcom/bui-react";
import { ArrowNavLeftIcon, MagnifyingGlassIcon, PlusIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useNavigate } from "@tanstack/react-router";
import { LoadingSpinner } from "../LoadingSpinner";
import { UserManagementErrorState } from "./UserManagementErrorState";
import { UserManagementTable } from "./UserManagementTable";
import { EditAccessModal } from "./EditAccessModal";
import { AddUserModal } from "./AddUserModal";
import { useUsersQuery } from "../../hooks/useUsersQuery";
import { useDebounce } from "../../hooks/useDebounce";
import type { PortalUser } from "../../types";
import styles from "./UserManagementPage.module.css";

export const UserManagementPage = () => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [editingUser, setEditingUser] = useState<PortalUser | null>(null);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 300);
  const isSearchTooShort = debouncedSearch.length > 0 && debouncedSearch.length < 3;

  const { data, isLoading, isError, isFetching, isPlaceholderData, refetch } = useUsersQuery({
    search: isSearchTooShort ? undefined : debouncedSearch || undefined,
    page,
  });

  const isUpdatingResults = isFetching && isPlaceholderData;

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setPage(0);
  }, []);

  return (
    <Container>
      <Box
        mixin={{
          paddingBlockStart: 8,
          paddingBlockEnd: 8,
          paddingInlineStart: 0,
          paddingInlineEnd: 0,
        }}
      >
        <Stack gap={8}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack gap={0}>
              <Text variant="headline_1">{i18n.trans(t("flights_airline_portal_user_management_title"))}</Text>
              <Text variant="body_2" color="neutral_alt">
                {i18n.trans(t("flights_airline_portal_user_management_subtitle"))}
              </Text>
            </Stack>
            <Button
              variant="secondary"
              icon={ArrowNavLeftIcon}
              size="large"
              onClick={() => navigate({ to: "/overview" })}
            >
              {i18n.trans(t("flights_airline_portal_superuser_back_button"))}
            </Button>
          </Stack>

          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="start">
              <div className={styles.searchContainer}>
                <InputText
                  name="search"
                  startIcon={<Icon svg={MagnifyingGlassIcon} />}
                  placeholder={i18n.trans(t("flights_airline_portal_user_management_search_placeholder"))}
                  size="large"
                  value={searchInput}
                  onChange={({ value }) => handleSearchChange(value)}
                  helper={
                    isSearchTooShort
                      ? i18n.trans(t("flights_airline_portal_user_management_search_helper"))
                      : " "
                  }
                />
              </div>
              <Button variant="primary" size="large" onClick={() => setIsAddUserOpen(true)} icon={PlusIcon}>
                {i18n.trans(t("flights_airline_portal_add_user_button"))}
              </Button>
            </Stack>

            {isLoading && !data ? (
              <LoadingSpinner />
            ) : isError ? (
              <UserManagementErrorState onRetry={() => refetch()} />
            ) : (
              <UserManagementTable
                users={data?.users ?? []}
                page={data?.page ?? 0}
                totalCount={data?.totalCount ?? 0}
                size={data?.size ?? 10}
                onPageChange={setPage}
                onEdit={setEditingUser}
                isUpdating={isUpdatingResults}
              />
            )}
          </Stack>
        </Stack>
      </Box>
      {editingUser && (
        <EditAccessModal key={editingUser.auth0UserId} user={editingUser} onClose={() => setEditingUser(null)} />
      )}
      {isAddUserOpen && <AddUserModal onClose={() => setIsAddUserOpen(false)} />}
    </Container>
  );
};
